import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDateTime, fmtNum } from "../lib/format";
import { ArrowLeft, Factory, ShoppingCart, AlertTriangle, ArrowDownLeft, ArrowUpRight, Box, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const CATEGORIES = [
  { key: "FABRICATION", label: "Fabriquées", icon: Factory, color: "#1F4E3D", bg: "#EAF4EE" },
  { key: "VENTE", label: "Vendues", icon: ShoppingCart, color: "#2D5A87", bg: "#E5EFF8" },
  { key: "PERTE_DLC", label: "Retirées DLC", icon: AlertTriangle, color: "#C05C47", bg: "#FCEEEA" },
  { key: "TRANSFER_IN", label: "Reçues (transfert)", icon: ArrowDownLeft, color: "#5847A6", bg: "#EFEAFA" },
  { key: "TRANSFER_OUT", label: "Envoyées (transfert)", icon: ArrowUpRight, color: "#8C6B2F", bg: "#FBF4E9" },
];

const WINDOWS = [
  { key: "24h", label: "24h" },
  { key: "7d", label: "7 jours" },
  { key: "30d", label: "30 jours" },
  { key: "90d", label: "90 jours" },
  { key: "365d", label: "1 an" },
];

const categoryMeta = (cat) => CATEGORIES.find((c) => c.key === cat) || { label: cat, color: "#575A57", bg: "#F0F0EB" };

export default function DistributorActivity() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filterCat, setFilterCat] = useState("ALL");

  useEffect(() => {
    api.get(`/distributors/${id}/activity`).then((r) => setData(r.data));
  }, [id]);

  const filteredEvents = useMemo(() => {
    if (!data) return [];
    if (filterCat === "ALL") return data.events;
    return data.events.filter((ev) => ev.category === filterCat);
  }, [data, filterCat]);

  if (!data) return <div className="text-[#575A57]" data-testid="activity-loading">Chargement…</div>;

  const dist = data.distributor;
  const isClient = dist.type === "CLIENT";

  return (
    <div className="space-y-6" data-testid="distributor-activity-page">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate("/distributeurs")} data-testid="back-to-distributors">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-heading text-[#1A1C1A]">{dist.name}</h1>
          <div className="flex items-center gap-2 text-sm text-[#575A57]">
            <span>Historique d'activité</span>
            {isClient && <Badge className="bg-[#EFEAFA] text-[#5847A6] border-[#D4C8F0]">Client</Badge>}
            {!isClient && <Badge variant="outline">Distributeur automatique</Badge>}
            {dist.code && <Badge variant="outline">{dist.code}</Badge>}
          </div>
        </div>
      </div>

      {/* Stock courant */}
      <Card className="border-[#E5E5E0]" data-testid="current-stock-card">
        <CardContent className="p-5 flex items-center gap-6">
          <div className="w-12 h-12 rounded-md bg-[#F0F0EB] flex items-center justify-center">
            <Box className="w-6 h-6 text-[#1F4E3D]" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide text-[#575A57]">Stock actuel dans ce dépôt</div>
            <div className="font-mono text-2xl text-[#1A1C1A]">
              {fmtNum(data.current_stock_quantity)} <span className="text-sm font-normal text-[#575A57]">unités · {data.current_barquettes} barquette{data.current_barquettes > 1 ? "s" : ""}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stat cards by category × window */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const stats = data.summary[cat.key] || {};
          return (
            <Card key={cat.key} className="border-[#E5E5E0]" data-testid={`stat-${cat.key.toLowerCase()}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: cat.bg }}>
                    <Icon className="w-4 h-4" style={{ color: cat.color }} />
                  </div>
                  <span className="text-sm font-medium text-[#1A1C1A]">{cat.label}</span>
                </div>
                <div className="space-y-1">
                  {WINDOWS.map((w) => (
                    <div key={w.key} className="flex items-center justify-between text-xs">
                      <span className="text-[#A3A6A3]">{w.label}</span>
                      <span className="font-mono text-[#1A1C1A]">{fmtNum(stats[w.key] || 0)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filter chips + timeline */}
      <div>
        <div className="flex items-center gap-2 flex-wrap mb-4">
          <button
            type="button"
            onClick={() => setFilterCat("ALL")}
            className={`px-3 py-1.5 text-xs rounded-full transition ${filterCat === "ALL" ? "bg-[#1F4E3D] text-white" : "bg-white border border-[#E5E5E0] text-[#575A57] hover:bg-[#F4F4F0]"}`}
            data-testid="filter-cat-all"
          >
            Tous ({data.events.length})
          </button>
          {CATEGORIES.map((cat) => {
            const count = data.events.filter((ev) => ev.category === cat.key).length;
            if (count === 0) return null;
            const Icon = cat.icon;
            return (
              <button
                key={cat.key}
                type="button"
                onClick={() => setFilterCat(cat.key)}
                className={`px-3 py-1.5 text-xs rounded-full transition flex items-center gap-1.5 ${filterCat === cat.key ? "text-white" : "bg-white border border-[#E5E5E0] text-[#575A57] hover:bg-[#F4F4F0]"}`}
                style={filterCat === cat.key ? { backgroundColor: cat.color } : {}}
                data-testid={`filter-cat-${cat.key.toLowerCase()}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {cat.label} ({count})
              </button>
            );
          })}
        </div>

        <Card className="border-[#E5E5E0]" data-testid="activity-timeline">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Catégorie</TableHead>
                  <TableHead>Produit</TableHead>
                  <TableHead>Lot</TableHead>
                  <TableHead className="text-right">Quantité</TableHead>
                  <TableHead>Détail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-[#A3A6A3]">
                      <Calendar className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      Aucun événement {filterCat !== "ALL" ? "dans cette catégorie" : "enregistré pour ce dépôt"}.
                    </TableCell>
                  </TableRow>
                ) : filteredEvents.map((ev) => {
                  const meta = categoryMeta(ev.category);
                  const Icon = meta.icon;
                  return (
                    <TableRow key={ev.id} data-testid={`activity-row-${ev.id}`}>
                      <TableCell className="font-mono text-xs text-[#575A57] whitespace-nowrap">{fmtDateTime(ev.created_at)}</TableCell>
                      <TableCell>
                        <Badge className="border" style={{ backgroundColor: meta.bg, color: meta.color, borderColor: meta.bg }}>
                          {Icon && <Icon className="w-3 h-3 mr-1" />}
                          {meta.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{ev.product_name || "—"}</div>
                        {ev.product_sku && <div className="text-[10px] text-[#A3A6A3] font-mono">{ev.product_sku}</div>}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {ev.lot_number ? (
                          <Link to={`/tracabilite?lot=${ev.lot_number}`} className="text-[#1F4E3D] hover:underline">
                            {ev.lot_number}
                          </Link>
                        ) : "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono">{fmtNum(ev.quantity, ev.product_unit)}</TableCell>
                      <TableCell className="text-xs text-[#575A57]">{ev.reason || "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
